'use client'

import type { ProposalTrade } from '@/components/molecules/proposal-account-details'

import { useCallback, useState } from 'react'

import Button, { ButtonVariant } from '@/components/atomic/button'
import Dialog from '@/components/atomic/dialog'
import { BodyDefault } from '@/components/atomic/text'

import { DialogContentContainer, DialogFooterContainer } from './styles'

interface ConfirmAllocationRemoveDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => Promise<void>
  tradeDetails: ProposalTrade
}

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default function ConfirmAllocationRemoveDialog({
  open,
  onOpenChange,
  onConfirm,
  tradeDetails,
}: ConfirmAllocationRemoveDialogProps) {
  const [isRemoving, setIsRemoving] = useState(false)

  const handleCloseDialog = useCallback(() => {
    onOpenChange(false)
  }, [onOpenChange])

  const handleConfirm = useCallback(async () => {
    setIsRemoving(true)
    try {
      await onConfirm()
      handleCloseDialog()
    } finally {
      setIsRemoving(false)
    }
  }, [onConfirm, handleCloseDialog])

  const handleInteractOutside = useCallback(
    (e: Event) => {
      if (isRemoving) {
        e.preventDefault()
      } else {
        handleCloseDialog()
      }
    },
    [isRemoving, handleCloseDialog],
  )

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      onInteractOutside={handleInteractOutside}
      title={`Remove allocation for "${tradeDetails.accountName}" from order for "${tradeDetails.name}"?`}
      mainContent={
        <DialogContentContainer>
          <BodyDefault>
            The allocation will be removed from this proposal. This action can
            not be undone.
          </BodyDefault>
        </DialogContentContainer>
      }
      footer={
        <DialogFooterContainer>
          <Button
            variant={ButtonVariant.Secondary}
            label="Go back"
            onClick={handleCloseDialog}
            disabled={isRemoving}
          />
          <Button
            variant={ButtonVariant.Primary}
            label={isRemoving ? 'Removing...' : 'Remove allocation'}
            onClick={handleConfirm}
            disabled={isRemoving}
            className="bg-moment-red-600 hover:bg-moment-red-700 dark:bg-moment-red-500 dark:hover:bg-moment-red-600"
          />
        </DialogFooterContainer>
      }
    />
  )
}
